import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency, formatDate, capitalizeFirst } from '../lib/utils';
import {
  GitBranch, Building2, MapPin, Users, Wallet, PiggyBank, Plus, ChevronRight,
  ChevronDown, Eye, Edit, MoreHorizontal, Phone, Mail, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationPage = () => {
  const [offices, setOffices] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    loadOffices();
  }, []);

  const loadOffices = async () => {
    setLoading(true);
    try {
      const [officesRes, hierarchyRes] = await Promise.all([
        axios.get(`${API_URL}/api/offices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/offices/hierarchy`, { withCredentials: true })
      ]);
      setOffices(officesRes.data);
      setHierarchy(hierarchyRes.data);
      
      // Auto-expand first 2 levels
      const expanded = {};
      hierarchyRes.data.forEach(o => { expanded[o.id] = true; });
      setExpandedNodes(expanded);
    } catch (error) {
      console.error('Failed to load offices:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewOffice = async (officeId) => {
    try {
      const response = await axios.get(`${API_URL}/api/offices/${officeId}`, { withCredentials: true });
      setSelectedOffice(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load office:', error);
    }
  };

  const toggleExpanded = useCallback((officeId) => {
    setExpandedNodes(prev => ({ ...prev, [officeId]: !prev[officeId] }));
  }, []);

  const getOfficeTypeIcon = (type) => {
    switch (type) {
      case 'head_office': return <Building2 className="w-5 h-5 text-[#C5A059]" />;
      case 'regional_office': return <MapPin className="w-5 h-5 text-blue-500" />;
      case 'branch': return <GitBranch className="w-5 h-5 text-emerald-500" />;
      case 'sub_branch': return <GitBranch className="w-4 h-4 text-gray-400" />;
      default: return <Building2 className="w-5 h-5 text-gray-400" />;
    }
  };

  const getOfficeTypeBadge = (type) => {
    const styles = {
      head_office: 'bg-[#C5A059]/10 text-[#C5A059] border-[#C5A059]/20',
      regional_office: 'bg-blue-50 text-blue-700 border-blue-200',
      branch: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      sub_branch: 'bg-gray-50 text-gray-700 border-gray-200'
    };
    return styles[type] || styles.branch;
  };

  const renderTreeNode = (office, level = 0) => {
    const hasChildren = office.children && office.children.length > 0;
    const isExpanded = expandedNodes[office.id];

    return (
      <div key={office.id} style={{ marginLeft: `${level * 24}px` }}>
        <div
          className={`flex items-center gap-3 p-3 border border-gray-100 hover:border-[#C5A059] transition-colors cursor-pointer ${
            level === 0 ? 'bg-gray-50' : 'bg-white'
          }`}
          onClick={() => viewOffice(office.id)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => { e.stopPropagation(); toggleExpanded(office.id); }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <div className="w-6" />
          )}
          
          {getOfficeTypeIcon(office.office_type)}
          
          <div className="flex-1">
            <p className="font-medium text-gray-900">{office.name}</p>
            <p className="text-xs text-gray-500">
              {office.city || 'N/A'} • {office.clients_count || 0} clients
            </p>
          </div>
          
          {hasChildren && (
            <span className="bg-gray-100 px-2 py-0.5 text-xs">
              {office.children.length} {office.children.length === 1 ? 'child' : 'children'}
            </span>
          )}
        </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-1 space-y-1">
            {office.children.map((child) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 fade-in" data-testid="organization-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
            ORGANIZATIONAL STRUCTURE
          </p>
          <h1 className="text-3xl font-bold text-[#1f1f1f]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Offices & Branches
          </h1>
          <p className="text-gray-500 mt-1">
            Manage your organizational hierarchy
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex border border-gray-200">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-4 py-2 text-sm ${viewMode === 'tree' ? 'bg-[#1f1f1f] text-white' : 'bg-white text-gray-600'}`}
            >
              Tree View
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm ${viewMode === 'list' ? 'bg-[#1f1f1f] text-white' : 'bg-white text-gray-600'}`}
            >
              List View
            </button>
          </div>
          <Button
            onClick={() => setShowNewModal(true)}
            className="bg-[#1f1f1f] hover:bg-[#333333]"
            data-testid="new-office-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Office
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="p-4 border border-gray-200 bg-[#1f1f1f] text-white">
          <p className="text-xs uppercase tracking-wider text-gray-400 mb-1">Total Offices</p>
          <p className="text-2xl font-bold">{offices.length}</p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Head Office</p>
          <p className="text-2xl font-bold text-[#C5A059]">
            {offices.filter(o => o.office_type === 'head_office').length}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Regional</p>
          <p className="text-2xl font-bold text-blue-600">
            {offices.filter(o => o.office_type === 'regional_office').length}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Branches</p>
          <p className="text-2xl font-bold text-emerald-600">
            {offices.filter(o => o.office_type === 'branch').length}
          </p>
        </div>
        <div className="p-4 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Total Clients</p>
          <p className="text-2xl font-bold text-[#1f1f1f]">
            {offices.reduce((sum, o) => sum + (o.clients_count || 0), 0)}
          </p>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" />
        </div>
      ) : viewMode === 'tree' ? (
        <div className="bg-white border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Office Hierarchy
          </h3>
          <div className="space-y-2">
            {hierarchy.map((office) => renderTreeNode(office, 0))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-hidden">
          <table className="w-full" data-testid="offices-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Office</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Location</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Children</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Clients</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {offices.map((office) => (
                <tr key={office.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {getOfficeTypeIcon(office.office_type)}
                      <div>
                        <p className="font-medium text-gray-900">{office.name}</p>
                        <p className="text-sm text-gray-500">{office.external_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-xs font-medium border ${getOfficeTypeBadge(office.office_type)}`}>
                      {capitalizeFirst(office.office_type?.replace(/_/g, ' '))}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{office.city}, {office.province}</td>
                  <td className="px-6 py-4 text-center text-sm font-medium">{office.children_count || 0}</td>
                  <td className="px-6 py-4 text-center text-sm font-medium">{office.clients_count || 0}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2.5 py-0.5 text-xs font-medium ${office.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}`}>
                      {capitalizeFirst(office.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => viewOffice(office.id)}>
                          <Eye className="w-4 h-4 mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Office Modal */}
      <NewOfficeModal open={showNewModal} onClose={() => setShowNewModal(false)} offices={offices} onSuccess={() => { setShowNewModal(false); loadOffices(); }} />

      {/* Office Detail Modal */}
      <OfficeDetailModal open={showDetailModal} onClose={() => setShowDetailModal(false)} office={selectedOffice} />
    </div>
  );
};

const NewOfficeModal = ({ open, onClose, offices, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', external_id: '', office_type: 'branch', parent_office_id: '',
    opening_date: '', address: '', city: '', province: '', phone: '', email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = { ...formData };
      if (data.parent_office_id === 'none' || !data.parent_office_id) {
        delete data.parent_office_id;
      }
      await axios.post(`${API_URL}/api/offices`, data, { withCredentials: true });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create office');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Poppins, sans-serif' }}>Add New Office</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Office Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">External ID</Label>
              <Input value={formData.external_id} onChange={(e) => setFormData(prev => ({ ...prev, external_id: e.target.value }))} placeholder="e.g., BR001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Office Type *</Label>
              <Select value={formData.office_type} onValueChange={(v) => setFormData(prev => ({ ...prev, office_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="head_office">Head Office</SelectItem>
                  <SelectItem value="regional_office">Regional Office</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                  <SelectItem value="sub_branch">Sub-Branch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Parent Office</Label>
              <Select value={formData.parent_office_id || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, parent_office_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select parent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Root)</SelectItem>
                  {offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Opening Date *</Label>
            <Input type="date" value={formData.opening_date} onChange={(e) => setFormData(prev => ({ ...prev, opening_date: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">City</Label>
              <Input value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Province</Label>
              <Select value={formData.province || 'select'} onValueChange={(v) => setFormData(prev => ({ ...prev, province: v === 'select' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="select" disabled>Select Province</SelectItem>
                  <SelectItem value="Gauteng">Gauteng</SelectItem>
                  <SelectItem value="Western Cape">Western Cape</SelectItem>
                  <SelectItem value="KwaZulu-Natal">KwaZulu-Natal</SelectItem>
                  <SelectItem value="Eastern Cape">Eastern Cape</SelectItem>
                  <SelectItem value="Free State">Free State</SelectItem>
                  <SelectItem value="Limpopo">Limpopo</SelectItem>
                  <SelectItem value="Mpumalanga">Mpumalanga</SelectItem>
                  <SelectItem value="North West">North West</SelectItem>
                  <SelectItem value="Northern Cape">Northern Cape</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]">{loading ? 'Creating...' : 'Create Office'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const OfficeDetailModal = ({ open, onClose, office }) => {
  if (!office) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Poppins, sans-serif' }} className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-[#C5A059]" />
            {office.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Office Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-24">Type:</span>
                  <span className="font-medium">{capitalizeFirst(office.office_type?.replace(/_/g, ' '))}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-24">External ID:</span>
                  <span className="font-medium">{office.external_id || '-'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">Opened:</span>
                  <span className="font-medium">{formatDate(office.opening_date)}</span>
                </div>
                {office.parent && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 w-24">Parent:</span>
                    <span className="font-medium">{office.parent.name}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Contact</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{office.city}, {office.province}</span>
                </div>
                {office.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /><span>{office.phone}</span></div>}
                {office.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /><span>{office.email}</span></div>}
              </div>
            </div>
          </div>
          {office.statistics && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Statistics</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 border border-gray-200 text-center">
                  <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#1f1f1f]">{office.statistics.clients_count}</p>
                  <p className="text-xs text-gray-500">Clients</p>
                </div>
                <div className="p-4 border border-gray-200 text-center">
                  <Wallet className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#1f1f1f]">{office.statistics.active_loans}</p>
                  <p className="text-xs text-gray-500">Active Loans</p>
                </div>
                <div className="p-4 border border-gray-200 text-center">
                  <PiggyBank className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-[#1f1f1f]">{office.statistics.active_accounts}</p>
                  <p className="text-xs text-gray-500">Accounts</p>
                </div>
                <div className="p-4 border border-gray-200 bg-[#1f1f1f] text-center">
                  <p className="text-lg font-bold text-[#C5A059]">{formatCurrency(office.statistics.loan_portfolio)}</p>
                  <p className="text-xs text-gray-400">Portfolio</p>
                </div>
              </div>
            </div>
          )}
          {office.children && office.children.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Child Offices ({office.children.length})</h4>
              <div className="space-y-2">
                {office.children.map((child) => (
                  <div key={child.id} className="flex items-center gap-3 p-3 border border-gray-100 hover:bg-gray-50">
                    <GitBranch className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{child.name}</span>
                    <span className="text-xs text-gray-500">{capitalizeFirst(child.office_type?.replace(/_/g, ' '))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrganizationPage;
